// src/pages/trading-portal/watchlist/WatchlistPage.js
// Full CRUD watchlist page following ContactsList pattern

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Chip,
    Menu,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Tooltip,
    Stack,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Clear as ClearIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    TrendingUp as TrendingUpIcon,
    Visibility as VisibilityIcon,
    CloudDownload as CloudDownloadIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    MarketType,
    MarketTypeLabels,
    MarketTypeColors,
    SymbolStatusLabels,
    SymbolStatusColors,
} from '../stockTradingEnums';

const WatchlistPage = () => {
    const navigate = useNavigate();

    // Permission checks
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // State
    const [symbols, setSymbols] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [marketFilter, setMarketFilter] = useState('');

    // Dialog states
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedSymbol, setSelectedSymbol] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);

    // Add Symbol search state
    const [symbolSearch, setSymbolSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [addingSymbol, setAddingSymbol] = useState(null);

    // Edit form state
    const [editForm, setEditForm] = useState({ notes: '', is_favorite: false });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-watchlist', 'view');
            const add = await hasPermission('tr-watchlist', 'add');
            const edit = await hasPermission('tr-watchlist', 'edit');
            const del = await hasPermission('tr-watchlist', 'delete');
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
            setCanDelete(del);
        };
        checkPermissions();
    }, []);

    // Fetch watchlist
    const fetchSymbols = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                page: page + 1,
                per_page: rowsPerPage,
            });

            if (searchQuery) params.append('search', searchQuery);
            if (marketFilter) params.append('market_type', marketFilter);

            const response = await axiosInstance.get(`/api/stock-trading/symbols?${params.toString()}`);

            if (response.data.success) {
                setSymbols(response.data.data || []);
                setTotalCount(response.data.pagination?.total || 0);
            } else {
                setError(response.data.message || 'Failed to fetch watchlist');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch watchlist');
        } finally {
            setLoading(false);
        }
    }, [canView, page, rowsPerPage, searchQuery, marketFilter]);

    useEffect(() => {
        if (canView) {
            fetchSymbols();
        }
    }, [fetchSymbols, canView]);

    // Handlers
    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };
    const handleSearch = (event) => { setSearchQuery(event.target.value); setPage(0); };
    const handleMarketFilterChange = (event) => { setMarketFilter(event.target.value); setPage(0); };
    const clearFilters = () => { setMarketFilter(''); setSearchQuery(''); setPage(0); };

    const handleMenuOpen = (event, symbol) => { setAnchorEl(event.currentTarget); setSelectedSymbol(symbol); };
    const handleMenuClose = () => setAnchorEl(null);

    // Add Symbol Dialog
    const handleAddDialogOpen = () => { setSymbolSearch(''); setSearchResults([]); setAddDialogOpen(true); };
    const handleAddDialogClose = () => { setAddDialogOpen(false); setSymbolSearch(''); setSearchResults([]); };

    const handleSymbolSearch = async () => {
        if (!symbolSearch.trim()) return;
        setSearching(true);
        try {
            const response = await axiosInstance.get(`/api/stock-trading/symbols/search?q=${encodeURIComponent(symbolSearch)}`);
            setSearchResults(response.data?.data || response.data?.results || []);
        } catch (err) {
            console.error('Symbol search failed:', err);
        } finally {
            setSearching(false);
        }
    };

    const handleSymbolSearchKeyDown = (e) => { if (e.key === 'Enter') handleSymbolSearch(); };

    const handleAddSymbol = async (result) => {
        const ticker = result.symbol || result.ticker;
        setAddingSymbol(ticker);
        try {
            const response = await axiosInstance.post('/api/stock-trading/symbols', {
                symbol: ticker,
                company_name: result.name || result.company_name,
                market_type: result.market_type || result.exchange || 'OTHER',
            });

            if (response.data.success) {
                const newSymbolId = response.data.data?.id;
                setSearchResults(prev => prev.filter(s => (s.symbol || s.ticker) !== ticker));

                // Auto-fetch latest price for the newly added symbol
                if (newSymbolId) {
                    try {
                        await axiosInstance.post(`/api/stock-trading/symbols/${newSymbolId}/fetch-price`, { timeframe: 'DAILY', limit: 1 });
                    } catch (priceErr) {
                        console.error('Auto-fetch price failed:', priceErr);
                    }
                }

                fetchSymbols();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add symbol');
        } finally {
            setAddingSymbol(null);
        }
    };

    // Edit Dialog
    const handleEditOpen = () => {
        if (selectedSymbol) {
            setEditForm({ notes: selectedSymbol.notes || '', is_favorite: selectedSymbol.is_favorite || false });
            setEditDialogOpen(true);
        }
        handleMenuClose();
    };

    const handleEditSubmit = async () => {
        if (!selectedSymbol) return;
        setSubmitting(true);
        try {
            const response = await axiosInstance.put(`/api/stock-trading/symbols/${selectedSymbol.id}`, editForm);
            if (response.data.success) {
                setEditDialogOpen(false);
                fetchSymbols();
            } else {
                setError(response.data.message || 'Failed to update symbol');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update symbol');
        } finally {
            setSubmitting(false);
        }
    };

    // Delete Dialog
    const handleDeleteClick = () => { setDeleteDialogOpen(true); handleMenuClose(); };

    const handleDeleteConfirm = async () => {
        if (!selectedSymbol) return;
        try {
            const response = await axiosInstance.delete(`/api/stock-trading/symbols/${selectedSymbol.id}`);
            if (response.data.success) {
                fetchSymbols();
                setDeleteDialogOpen(false);
                setSelectedSymbol(null);
            } else {
                setError(response.data.message || 'Failed to archive symbol');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to archive symbol');
        }
    };

    // Fetch latest price
    const handleFetchPrice = async () => {
        if (!selectedSymbol) return;
        handleMenuClose();
        try {
            await axiosInstance.post(`/api/stock-trading/symbols/${selectedSymbol.id}/fetch-price`, { timeframe: 'DAILY', limit: 1 });
            fetchSymbols();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch price');
        }
    };

    // Toggle favorite
    const handleToggleFavorite = async (symbol, e) => {
        e.stopPropagation();
        try {
            await axiosInstance.put(`/api/stock-trading/symbols/${symbol.id}`, { is_favorite: !symbol.is_favorite });
            fetchSymbols();
        } catch (err) {
            console.error('Failed to toggle favorite:', err);
        }
    };

    const getPriceChangeColor = (change) => {
        if (!change) return '#94a3b8';
        return change >= 0 ? '#10b981' : '#ef4444';
    };

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view the watchlist.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h4" component="h1">Watchlist</Typography>
                        <Chip label={totalCount} color="primary" size="small" />
                    </Box>
                    {canAdd && (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddDialogOpen} sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
                            Add Symbol
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search symbols..."
                            value={searchQuery}
                            onChange={handleSearch}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchQuery('')}><ClearIcon /></IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Market Type</InputLabel>
                            <Select value={marketFilter} label="Market Type" onChange={handleMarketFilterChange}>
                                <MenuItem value="">All Markets</MenuItem>
                                {Object.values(MarketType).map((type) => (
                                    <MenuItem key={type} value={type}>{MarketTypeLabels[type]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={1}>
                            <Tooltip title="Clear Filters"><IconButton onClick={clearFilters}><ClearIcon /></IconButton></Tooltip>
                            <Tooltip title="Refresh"><IconButton onClick={fetchSymbols}><RefreshIcon /></IconButton></Tooltip>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Error Alert */}
            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Symbols Table */}
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Symbol</TableCell>
                                <TableCell>Company</TableCell>
                                <TableCell align="right">Price</TableCell>
                                <TableCell align="right">Change %</TableCell>
                                <TableCell align="right">Volume</TableCell>
                                <TableCell>Market</TableCell>
                                <TableCell align="center">Favorite</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                                </TableRow>
                            ) : symbols.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">No symbols found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                symbols.map((symbol) => (
                                    <TableRow key={symbol.id} hover sx={{ cursor: 'pointer' }}>
                                        <TableCell>
                                            <Typography variant="body1" fontWeight="bold">{symbol.symbol}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{symbol.company_name || '-'}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="medium">
                                                {symbol.current_price != null ? `$${parseFloat(symbol.current_price).toFixed(2)}` : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {symbol.change_percent != null ? (
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="medium"
                                                    sx={{ color: getPriceChangeColor(symbol.change_percent) }}
                                                >
                                                    {symbol.change_percent >= 0 ? '+' : ''}{parseFloat(symbol.change_percent).toFixed(2)}%
                                                </Typography>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                {symbol.volume != null ? parseInt(symbol.volume).toLocaleString() : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={MarketTypeLabels[symbol.market_type] || symbol.market_type}
                                                size="small"
                                                color={MarketTypeColors[symbol.market_type] || 'default'}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleToggleFavorite(symbol, e)}
                                                sx={{ color: symbol.is_favorite ? '#f59e0b' : '#94a3b8' }}
                                            >
                                                {symbol.is_favorite ? <StarIcon /> : <StarBorderIcon />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, symbol); }}>
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </Paper>

            {/* Actions Menu */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={() => { handleMenuClose(); navigate('/trading-portal/historical', { state: { symbolId: selectedSymbol?.id } }); }}>
                    <VisibilityIcon sx={{ mr: 1 }} fontSize="small" /> View Details
                </MenuItem>
                <MenuItem onClick={handleFetchPrice}>
                    <CloudDownloadIcon sx={{ mr: 1 }} fontSize="small" /> Fetch Latest Price
                </MenuItem>
                {canEdit && (
                    <MenuItem onClick={handleEditOpen}>
                        <EditIcon sx={{ mr: 1 }} fontSize="small" /> Edit
                    </MenuItem>
                )}
                {canDelete && (
                    <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                        <DeleteIcon sx={{ mr: 1 }} fontSize="small" /> Delete
                    </MenuItem>
                )}
            </Menu>

            {/* Add Symbol Dialog */}
            <Dialog open={addDialogOpen} onClose={handleAddDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle>Add Symbol to Watchlist</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by ticker or company name..."
                        value={symbolSearch}
                        onChange={(e) => setSymbolSearch(e.target.value)}
                        onKeyDown={handleSymbolSearchKeyDown}
                        sx={{ mt: 1, mb: 2 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                            endAdornment: (
                                <InputAdornment position="end">
                                    {searching ? <CircularProgress size={18} /> : (
                                        <Button size="small" onClick={handleSymbolSearch}>Search</Button>
                                    )}
                                </InputAdornment>
                            ),
                        }}
                    />
                    {searchResults.length > 0 ? (
                        <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                            {searchResults.map((result, index) => (
                                <ListItem key={index} sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <ListItemText
                                        primary={<Typography fontWeight="bold">{result.symbol || result.ticker}</Typography>}
                                        secondary={result.name || result.company_name}
                                    />
                                    <ListItemSecondaryAction>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={addingSymbol === (result.symbol || result.ticker) ? <CircularProgress size={14} /> : <AddIcon />}
                                            onClick={() => handleAddSymbol(result)}
                                            disabled={addingSymbol === (result.symbol || result.ticker)}
                                            sx={{ borderColor: '#f59e0b', color: '#f59e0b', '&:hover': { borderColor: '#d97706', bgcolor: '#fffbeb' } }}
                                        >
                                            Add
                                        </Button>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    ) : symbolSearch && !searching ? (
                        <Box sx={{ py: 3, textAlign: 'center' }}>
                            <Typography color="text.secondary">No results. Press Enter or click Search.</Typography>
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAddDialogClose}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Edit Symbol Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit {selectedSymbol?.symbol}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Notes"
                        multiline
                        rows={3}
                        value={editForm.notes}
                        onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <FormControl fullWidth>
                        <InputLabel>Favorite</InputLabel>
                        <Select
                            value={editForm.is_favorite ? 'yes' : 'no'}
                            label="Favorite"
                            onChange={(e) => setEditForm(prev => ({ ...prev, is_favorite: e.target.value === 'yes' }))}
                        >
                            <MenuItem value="yes">Yes</MenuItem>
                            <MenuItem value="no">No</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)} disabled={submitting}>Cancel</Button>
                    <Button variant="contained" onClick={handleEditSubmit} disabled={submitting}
                        sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
                        {submitting ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Archive Symbol</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to archive <strong>{selectedSymbol?.symbol}</strong>?
                        This will remove it from your active watchlist.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Archive</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WatchlistPage;
